import { SelectChipGroup } from '@lastbrand/web-ui/select-chip'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import { type ComponentProps, useId, useMemo } from 'react'

import { chipGroupCss } from '@src/components/blocks/option-selector-ds/text-chip-group-ds.styles'
import { ProductPropertyDS } from '@src/components/blocks/product-property-ds'
import { getProductLinkWithSlug } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import { AddOnProductOptionGroup } from '@src/quince-site-common/src/domains/AddOnProductOptionGroup'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { getDisplayKey } from '@src/utils/i18n.utils'

import SiblingProductButtonDS from './sibling-product-button-ds'
import { optionRowDsClass } from './sibling-product-options-ds.styles'

export enum SiblingProductOptionType {
  LINK = 'LINK',
  BUTTON = 'BUTTON',
}

// Radix Root only when the chips are actual toggle items (BUTTON). LINK rows render their
// recipe-styled anchors bare: a Root around non-item children adds only a spurious
// role="group" + a label duplicating ProductPropertyDS's — the double-announcement flagged
// in the PR #7895 review.
function MaybeSelectChipGroup({
  enabled,
  children,
  ...groupProps
}: ComponentProps<typeof SelectChipGroup> & { enabled: boolean }) {
  return enabled ? <SelectChipGroup {...groupProps}>{children}</SelectChipGroup> : <>{children}</>
}

interface SiblingProductOptionsProps {
  siblingProductOptionGroup: AddOnProductOptionGroup
  displayType: SiblingProductOptionType
  selectedProductOptions?: ProductVariantOption[]
  onSelect?: (val: string, productId?: number) => void
  selectedProductId?: number
  className?: string
  labelClassName?: string
  optionSelectorClassName?: string
  optionClassname?: string
  isMiniPDP?: boolean
}

export default function SiblingProductOptionsDS({
  siblingProductOptionGroup,
  onSelect,
  displayType,
  selectedProductId,
  selectedProductOptions = [],
  className,
  labelClassName,
  optionSelectorClassName,
  optionClassname,
  isMiniPDP,
}: SiblingProductOptionsProps) {
  const isNewLocalisedFlow = useI18nPreviewContext()
  // No longer used for the header id / aria-labelledby wiring — ProductPropertyDS owns that
  // internally (its own useId). Kept only as a unique prefix for each sibling button's id.
  const labelId = useId()

  const selectedGroupItemLabel = useMemo(() => {
    const selectedOption = siblingProductOptionGroup.groupItems.find(groupItem => groupItem.spid === selectedProductId)
    return selectedOption
      ? getDisplayKey(
          selectedOption,
          'mergeOptionValueDisplayName',
          selectedOption.mergeOptionValue,
          isNewLocalisedFlow,
        )
      : ''
  }, [siblingProductOptionGroup.groupItems, selectedProductId, isNewLocalisedFlow])

  const groupLabel = getDisplayKey(
    siblingProductOptionGroup,
    'groupOptionDisplayName',
    siblingProductOptionGroup.groupOptionName,
    isNewLocalisedFlow,
  )

  return (
    // className moved to this root (was on the grandchild ProductPropertyDS, invisible to parent margin zeroing — STRFRNT-16897/16891)
    <div data-testid="sibling-product-options" className={className}>
      <ProductPropertyDS
        as="div"
        headerClassName={labelClassName}
        size={isMiniPDP ? 'sm' : 'md'}
        label={groupLabel}
        selectedValue={selectedGroupItemLabel || undefined}
        contentClassName={cx(optionRowDsClass, optionSelectorClassName)}
      >
        <MaybeSelectChipGroup
          // LINK chips are plain recipe-styled anchors (no Radix items), so the chip group would
          // contribute nothing but an extra role="group" + duplicated label to the tree — only
          // the BUTTON variant, a genuine single-select, gets the Radix Root (PR #7895 review).
          enabled={displayType === SiblingProductOptionType.BUTTON}
          type="single"
          // Radix ToggleGroup 'single' deselects on re-click (fires with '') — swallow the
          // empty value; only the BUTTON variant selects via the group (LINK navigates instead).
          value={selectedProductId !== undefined ? String(selectedProductId) : ''}
          onValueChange={value => {
            if (!value || displayType !== SiblingProductOptionType.BUTTON) return
            const groupItem = siblingProductOptionGroup.groupItems.find(item => String(item.spid) === value)
            if (!groupItem || groupItem.spid === selectedProductId) return
            onSelect?.(value, groupItem.productId)
          }}
          aria-label={groupLabel}
          css={chipGroupCss}
        >
          {siblingProductOptionGroup.groupItems.map((groupItem, idx) => {
            return (
              <SiblingProductButtonDS
                id={`sibling-product-option-${labelId}-${idx}`}
                testId={`sibling-product-option-${idx}`}
                key={groupItem.spid}
                selectionValue={String(groupItem.spid)}
                value={
                  displayType === SiblingProductOptionType.LINK
                    ? getProductLinkWithSlug(groupItem.productHandle, selectedProductOptions, {
                        includeSize: true,
                        isMiniPDP,
                      })
                    : String(groupItem.spid)
                }
                displayName={getDisplayKey(
                  groupItem,
                  'mergeOptionValueDisplayName',
                  groupItem.mergeOptionValue,
                  isNewLocalisedFlow,
                )}
                isActive={groupItem.spid === selectedProductId}
                variant={displayType}
                onClickCallback={val => onSelect?.(val, groupItem.productId)}
                className={optionClassname}
              />
            )
          })}
        </MaybeSelectChipGroup>
      </ProductPropertyDS>
    </div>
  )
}
